import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Database, Target, Zap, AlertTriangle, TrendingUp } from 'lucide-react';
import { fetchPrecisionOverview } from '../data/adapters';
import { PrecisionOverview } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const PrecisionDataTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [data, setData] = useState<PrecisionOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const precisionData = await fetchPrecisionOverview(isDemo).catch(() => null);
                setData(precisionData);
                if (!precisionData) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const getConfidenceColor = (confidence: number) => {
        if (confidence >= 0.8) return 'text-green-400';
        if (confidence >= 0.6) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getConfidenceLabel = (confidence: number) => {
        if (confidence >= 0.8) return 'High';
        if (confidence >= 0.6) return 'Medium';
        return 'Low';
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* Data Quality Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || !data) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Mapping Coverage</h3>
                            <Target size={20} className="text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">
                                Transform Accuracy
                            </h3>
                            <Zap size={20} className="text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Lookup Hit Rate</h3>
                            <Database size={20} className="text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Low Confidence Fields</h3>
                    {renderNoDataPlaceholder('Field confidence data')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Coverage Breakdown
                        </h3>
                        {renderNoDataPlaceholder('Coverage metrics')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                        {renderNoDataPlaceholder('Recommendations')}
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Mapping Coverage */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Mapping Coverage</h3>
                        <Target size={20} className="text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {data.mappingCoveragePct}%
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${data.mappingCoveragePct}%` }}
                        />
                    </div>
                    <p className="text-sm text-slate-400">Fields successfully mapped</p>
                </motion.div>

                {/* Transform Accuracy */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Transform Accuracy</h3>
                        <Zap size={20} className="text-green-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {data.transformAccuracyPct}%
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                        <div
                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${data.transformAccuracyPct}%` }}
                        />
                    </div>
                    <p className="text-sm text-slate-400">Transformations passing validation</p>
                </motion.div>

                {/* Lookup Hit Rate */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Lookup Hit Rate</h3>
                        <Database size={20} className="text-purple-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {data.lookupHitRatePct}%
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                        <div
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${data.lookupHitRatePct}%` }}
                        />
                    </div>
                    <p className="text-sm text-slate-400">Successful reference lookups</p>
                </motion.div>
            </div>

            {/* Low Confidence Fields */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="evolut-metric-card p-6"
            >
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Low Confidence Fields</h3>
                    <div className="flex items-center space-x-2 text-sm text-slate-400">
                        <AlertTriangle size={16} />
                        <span>Fields requiring attention</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {data.lowConfidenceFields.map((field, index) => (
                        <motion.div
                            key={field.field}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-white font-mono text-sm">
                                    {field.field}
                                </h4>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(field.confidence)} bg-slate-700/50`}
                                >
                                    {getConfidenceLabel(field.confidence)}
                                </span>
                            </div>

                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400">
                                    Confidence: {(field.confidence * 100).toFixed(1)}%
                                </span>
                                <span className="text-slate-400">
                                    {field.sampleCount.toLocaleString()} samples
                                </span>
                            </div>

                            <div className="w-full bg-slate-700 rounded-full h-1.5 mt-2">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-500 ${
                                        field.confidence >= 0.8
                                            ? 'bg-green-500'
                                            : field.confidence >= 0.6
                                              ? 'bg-yellow-500'
                                              : 'bg-red-500'
                                    }`}
                                    style={{ width: `${field.confidence * 100}%` }}
                                />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Coverage Breakdown */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Coverage Breakdown</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Mapping Coverage</span>
                            <span className="text-white font-semibold">
                                {data.mappingCoveragePct}%
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Transform Accuracy</span>
                            <span className="text-white font-semibold">
                                {data.transformAccuracyPct}%
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Lookup Hit Rate</span>
                            <span className="text-white font-semibold">
                                {data.lookupHitRatePct}%
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Low Confidence Fields</span>
                            <span className="text-white font-semibold">
                                {data.lowConfidenceFields.length}
                            </span>
                        </div>
                    </div>
                </motion.div>

                {/* Recommendations */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                    <div className="space-y-4">
                        {data.mappingCoveragePct < 90 && (
                            <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                                <Target size={16} className="text-yellow-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-yellow-300">
                                        Improve Mapping Coverage
                                    </h4>
                                    <p className="text-sm text-yellow-200">
                                        Current coverage is below 90%. Review unmapped fields and
                                        update mapping rules.
                                    </p>
                                </div>
                            </div>
                        )}

                        {data.transformAccuracyPct < 95 && (
                            <div className="flex items-start space-x-3 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                                <Zap size={16} className="text-orange-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-orange-300">
                                        Enhance Transform Validation
                                    </h4>
                                    <p className="text-sm text-orange-200">
                                        Transform accuracy below 95%. Review failing transformations
                                        and update validation rules.
                                    </p>
                                </div>
                            </div>
                        )}

                        {data.lowConfidenceFields.length > 5 && (
                            <div className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                                <AlertTriangle size={16} className="text-red-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-red-300">
                                        Address Low Confidence Fields
                                    </h4>
                                    <p className="text-sm text-red-200">
                                        {data.lowConfidenceFields.length} fields have low
                                        confidence. Review data quality and mapping rules.
                                    </p>
                                </div>
                            </div>
                        )}

                        {data.mappingCoveragePct >= 90 &&
                            data.transformAccuracyPct >= 95 &&
                            data.lowConfidenceFields.length <= 5 && (
                                <div className="flex items-start space-x-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                                    <TrendingUp size={16} className="text-green-400 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-green-300">
                                            Excellent Data Quality
                                        </h4>
                                        <p className="text-sm text-green-200">
                                            All precision metrics are within acceptable ranges.
                                            Continue monitoring for any degradation.
                                        </p>
                                    </div>
                                </div>
                            )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
